import type { Query, QueryClient } from '@tanstack/react-query';
import { ALL_GRAPHS } from '@/components/GraphSelector';

/** After any data write — file load, spreadsheet/folder/Gremlin import, graph
 *  create/clear/delete — drop every cached read. A data write can change almost
 *  any surface (Browse `['dataset']`, Catalog `['catalog']`, Query, VDB/Viz,
 *  vocab trees, entity pages, dashboards, the transaction log), and selective
 *  per-surface key lists have twice shipped stale-cache bugs when a surface's
 *  key was missing from one of them. Data writes are rare and heavyweight;
 *  refetching the handful of mounted queries is the cheap side of the trade.
 *  Every data-writing surface calls THIS — never a hand-rolled key list. */
export function invalidateAfterDataWrite(queryClient: QueryClient): void {
  void queryClient.invalidateQueries();
}

// Reads no edit to a term or a statement can change: who is signed in, the
// site's settings, the database and graph lists, the prefix registry, rule
// definitions and saved queries. The index status is refreshed: an edit starts
// a rebuild, and a status polled once a minute while ready would not show it.
// Listed by what they are NOT, so a new data read is refreshed by default and
// a missing entry can only cost a refetch, never a stale page.
const NOT_CHANGED_BY_AN_EDIT = new Set<string>([
  'me',
  'health',
  'branding',
  'admin',
  'pages-config',
  'modules-config',
  'entitled-modules',
  'session-idle-minutes',
  'query-timeout-secs',
  'max-load-bytes',
  'marketing-panes',
  'issues-enabled',
  'anonymous-read',
  'dev-banner',
  'qros-cli-downloads',
  'property-graph-import-enabled',
  'query-log-grant',
  'query-log',
  'snippets',
  'databases',
  'graphs',
  'namespaces',
  'git-sources',
  'import-template',
  'load-gate',
  'rules',
  'rules-name-audit',
]);

/** Whether an edit written to `graph` can change the read cached under `key`. */
export function editAffects(key: readonly unknown[], graph: string | null): boolean {
  const [root, second, third] = key;
  if (typeof root !== 'string' || NOT_CHANGED_BY_AN_EDIT.has(root)) return false;
  // A schema read is per graph: an edit in one graph leaves the others' as
  // they were, and the all-graphs read holds every graph.
  if (root === 'ontology' && second === 'schema' && graph !== null) {
    return third === graph || third === ALL_GRAPHS;
  }
  return true;
}

/** After an edit to a term or its statements — a class, property, vocabulary
 *  term, label or statement changed, created, renamed, merged or deleted —
 *  refresh the reads it can change: the schema of the graph written and of all
 *  graphs, entity pages, Browse, the catalog, vocabulary trees, the index
 *  status and search, and the dashboard. `graph` is the graph the edit was written to, or
 *  null when the edit can reach any graph's schema. */
export function invalidateAfterEdit(queryClient: QueryClient, graph: string | null): void {
  void queryClient.invalidateQueries({
    predicate: (query: Query) => editAffects(query.queryKey, graph),
  });
}
